import type React from 'react';
import type { AutoSaveStatus } from '../hooks/useAutoSave';
import { getAutoSaveStatusDisplay } from './autoSaveStatusDisplay';

interface AutoSavePillProps {
  status: AutoSaveStatus;
  /**
   * 'autosave' shows "Autosaved" after saves. 'manual' is for forms with an
   * explicit save button: "Saved" after saves. Both hide the pill when idle.
   */
  variant?: 'autosave' | 'manual';
  /**
   * Debounce window of the pending autosave. When set, the unsaved state
   * shows a bar along the pill's bottom edge that fills over this duration,
   * reaching full width when the save fires.
   */
  debounceMs?: number;
  /**
   * Increment on every edit that resets the debounce timer so the bar
   * restarts in sync with it.
   */
  changeToken?: number;
  className?: string;
}

const PILL_STYLES: Record<AutoSaveStatus, string> = {
  idle: 'border-border bg-background/95 text-muted-foreground',
  unsaved: 'border-amber-300 bg-amber-50 text-amber-700',
  saving: 'border-border bg-background/95 text-muted-foreground',
  blocked: 'border-border bg-background/95 text-muted-foreground',
  saved: 'border-green-200 bg-green-50 text-green-700',
  error: 'border-red-200 bg-red-50 text-red-600',
};

/**
 * Persistent autosave status pill. A pure function of the reported status;
 * producers latch their last outcome (saved/error) until the next edit, so
 * the pill stays informative without local memory. Pair with a sticky
 * wrapper to keep it in view on long forms.
 */
export const AutoSavePill: React.FC<AutoSavePillProps> = ({
  status,
  variant = 'autosave',
  debounceMs,
  changeToken,
  className = '',
}) => {
  // The pill is hidden while idle (an "Autosave on" badge is redundant), but
  // the aria-live element must stay mounted: screen readers only announce
  // changes inside live regions that already exist in the accessibility tree.
  const showPill = status !== 'idle';

  const outcome = getAutoSaveStatusDisplay(status);
  const label =
    status === 'saved' && variant === 'autosave' ? 'Autosaved' : outcome?.label;

  return (
    <div aria-live='polite' className={className}>
      {showPill && (
        <div
          className={`pointer-events-none relative flex items-center gap-1.5 overflow-hidden rounded-full border px-3 py-1 text-xs font-medium shadow-sm ${PILL_STYLES[status]}`}
        >
          {status === 'unsaved' && debounceMs != null && debounceMs > 0 && (
            <span
              key={changeToken}
              data-testid='autosave-debounce-progress'
              // Hidden (not paused) under reduced motion: a static bar would
              // read as an already-complete countdown
              className='absolute bottom-0 left-0 h-0.5 w-full origin-left animate-autosave-progress bg-amber-500 motion-reduce:hidden'
              style={{ animationDuration: `${debounceMs}ms` }}
            />
          )}
          {status === 'unsaved' && 'Saving...'}
          {outcome && label && (
            <>
              <outcome.Icon
                className={`h-3.5 w-3.5${outcome.spin ? ' animate-spin' : ''}`}
              />
              {label}
            </>
          )}
        </div>
      )}
    </div>
  );
};
